import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card } from "./ui/card";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Loader2, RefreshCw, CloudRain, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CancellationTemplate = () => {
  const [tpl, setTpl] = useState(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/whatsapp/cancellation-template`, { withCredentials: true });
      setTpl(r.data);
      setDraft(r.data.body || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the cancellation template");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const register = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/whatsapp/cancellation-template`,
        { body: draft }, { withCredentials: true });
      toast.success(r.data.already ? "Already registered with WhatsApp" : "Submitted for approval");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  const status = tpl?.status || "";
  const tone = status === "approved"
    ? "bg-emerald-500/20 text-emerald-300"
    : status === "rejected" ? "bg-red-500/20 text-red-300" : "bg-amber-500/20 text-amber-300";

  return (
    <Card className="p-5 bg-slate-900/50" data-testid="cancellation-template-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <Label className="text-white font-semibold flex items-center gap-2">
            <CloudRain className="w-4 h-4 text-sky-400" /> Rain / haze cancellation
          </Label>
          <p className="text-xs text-slate-400 mt-0.5">
            Sent automatically to every family booked that day — term players and trialists —
            the moment you cancel a session.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tpl?.registered && (
            <Badge className={`text-[10px] ${tone}`} data-testid="cancellation-template-status">
              {status || "pending"}
            </Badge>
          )}
          <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 h-7"
            onClick={load} disabled={loading} data-testid="cancellation-template-refresh">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </Button>
        </div>
      </div>
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={5}
        className="bg-slate-950 border-slate-700 text-white font-mono text-xs"
        data-testid="cancellation-template-body"
      />
      <p className="text-xs text-slate-500 mt-1">
        {"{{1}}"} parent · {"{{2}}"} child · {"{{3}}"} date · {"{{4}}"} reason · {"{{5}}"} closing line.
        Term parents are told a make-up session may follow; trialists are asked to reply
        REBOOK, which flags their trial for a new slot. Nothing is said about credits.
      </p>
      {tpl?.rejection_reason && (
        <p className="text-xs text-red-300 mt-1">WhatsApp said: {tpl.rejection_reason}</p>
      )}
      <Button size="sm" onClick={register} disabled={busy || !draft.trim()}
        className="bg-emerald-600 hover:bg-emerald-700 mt-2" data-testid="cancellation-template-register">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
        {tpl?.registered ? "Re-submit wording" : "Register with WhatsApp"}
      </Button>
    </Card>
  );
};
